import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../hooks/useAuth';
import { useData } from '../contexts/DataContext';
import { useSupabaseClient } from '../lib/supabaseClient';
import Navbar from '../components/layout/Navbar';
import Modal from '../components/ui/Modal';
import StatusBadge from '../components/ui/StatusBadge';
import Toggle from '../components/ui/Toggle';
import ClientForm from '../components/forms/ClientForm';
import { getProfileImageUrl } from '../utils/profileImage';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import logDev from '../utils/logDev';

const { FiPlus, FiSearch, FiEdit, FiTrash2, FiEye, FiMail, FiPhone, FiMapPin, FiCalendar, FiBriefcase, FiArchive, FiDollarSign, FiToggleRight, FiGrid, FiList, FiFilter } = FiIcons;

const AGENT_ELIGIBLE_ROLES = [
  'advisor',
  'representative',
  'associate',
  'senior_associate',
  'marketing_director',
  'smd',
  'emd',
  'admin',
];

const AGENT_PROFILE_ERROR =
  'Your agent profile is not set up. Please contact an administrator.';

const sanitizeNamePart = (value) => {
  if (value === null || value === undefined) {
    return '';
  }
  return String(value).trim();
};

const getClientNameInfo = (client) => {
  if (!client) {
    return {
      firstName: '',
      middleName: '',
      lastName: '',
      displayName: '',
      legacyName: '',
    };
  }

  const firstName = sanitizeNamePart(client.firstName ?? client.first_name);
  const middleName = sanitizeNamePart(client.middleName ?? client.middle_name);
  const lastName = sanitizeNamePart(client.lastName ?? client.last_name);
  const displayName = [firstName, middleName, lastName].filter(Boolean).join(' ');
  const legacyName = sanitizeNamePart(client.name);

  return {
    firstName,
    middleName,
    lastName,
    displayName,
    legacyName,
  };
};

const formatClientName = (client, nameInfo) => {
  const info = nameInfo ?? getClientNameInfo(client);
  return info.displayName || info.legacyName;
};

const normalizeForSearch = (value) => sanitizeNamePart(value).toLowerCase();

const normalizeRelationshipData = (client = {}) => {
  const spouseSource = client.spouse_info ?? client.spouseInfo ?? client.spouse ?? {};
  const childrenSource = client.children_info ?? client.childrenInfo ?? client.children ?? [];

  const normalizedSpouse = {
    name: spouseSource?.name ?? '',
    email: spouseSource?.email ?? '',
    phone: spouseSource?.phone ?? '',
    date_of_birth: spouseSource?.date_of_birth ?? spouseSource?.dateOfBirth ?? '',
    gender: spouseSource?.gender ?? '',
    employer_name: spouseSource?.employer_name ?? spouseSource?.employerName ?? '',
  };

  const normalizedChildren = Array.isArray(childrenSource)
    ? childrenSource.map((child) => ({
        name: child?.name ?? '',
        date_of_birth: child?.date_of_birth ?? child?.dateOfBirth ?? '',
      }))
    : [];

  return {
    ...client,
    spouse_info: normalizedSpouse,
    children_info: normalizedChildren,
  };
};

const ClientManagement = () => {
  const { user } = useAuth();
  const { clients, users, addClient, updateClient, deleteClient } = useData();
  const supabase = useSupabaseClient();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [sortOption, setSortOption] = useState('date-modified');
  const [advisorFilter, setAdvisorFilter] = useState('');
  const [maritalStatusFilter, setMaritalStatusFilter] = useState('');
  const [hasChildrenFilter, setHasChildrenFilter] = useState('');
  const [portalAccessFilter, setPortalAccessFilter] = useState('');
  const [createdDateRange, setCreatedDateRange] = useState({ start: '', end: '' });
  const [updatedDateRange, setUpdatedDateRange] = useState({ start: '', end: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [, setIsSubmitting] = useState(false);
  const [agentTrackerId, setAgentTrackerId] = useState(null);
  const [agentTrackerError, setAgentTrackerError] = useState('');

  useEffect(() => {
    const fetchAgentTrackerId = async () => {
      const authUserId = user?.authId || user?.supabaseId || user?.id;

      if (!supabase || !authUserId) return;

      if (user?.role && !AGENT_ELIGIBLE_ROLES.includes(user.role)) {
        setAgentTrackerId(null);
        setAgentTrackerError('');
        return;
      }

      try {
        setAgentTrackerError('');
        const { data: userProfile, error: userProfileError } = await supabase
          .from('users_pf')
          .select('id')
          .eq('auth_id', authUserId)
          .maybeSingle();

        if (userProfileError) {
          throw userProfileError;
        }

        const profileId = userProfile?.id;

        if (!profileId) {
          setAgentTrackerId(null);
          setAgentTrackerError(AGENT_PROFILE_ERROR);
          return;
        }

        const { data, error } = await supabase
          .from('agent_tracker')
          .select('id')
          .eq('user_id', profileId)
          .maybeSingle();

        if (error) {
          throw error;
        }

        if (!data?.id) {
          setAgentTrackerId(null);
          setAgentTrackerError(AGENT_PROFILE_ERROR);
          return;
        }

        setAgentTrackerId(data.id);
      } catch (err) {
        console.error('Error fetching agent tracker ID:', err);
        setAgentTrackerId(null);
        setAgentTrackerError(AGENT_PROFILE_ERROR);
      }
    };

    fetchAgentTrackerId();
  }, [supabase, user?.authId, user?.supabaseId, user?.id, user?.role]);

  const advisors = useMemo(
    () => (users || []).filter((item) => item.role === 'advisor'),
    [users]
  );

  const maritalStatusOptions = useMemo(() => {
    const statuses = new Set();
    (clients || []).forEach((client) => {
      const status = client?.maritalStatus || client?.marital_status;
      if (status) {
        statuses.add(status.toString().toLowerCase());
      }
    });
    return Array.from(statuses).sort();
  }, [clients]);

  const hasActiveFilters = useMemo(
    () =>
      Boolean(
        advisorFilter ||
          maritalStatusFilter ||
          hasChildrenFilter ||
          portalAccessFilter ||
          createdDateRange.start ||
          createdDateRange.end ||
          updatedDateRange.start ||
          updatedDateRange.end
      ),
    [
      advisorFilter,
      maritalStatusFilter,
      hasChildrenFilter,
      portalAccessFilter,
      createdDateRange.start,
      createdDateRange.end,
      updatedDateRange.start,
      updatedDateRange.end
    ]
  );

  const toggleButtonClasses = (isActive) =>
    `flex-1 px-3 py-2 text-sm font-medium rounded-md border transition-colors ${
      isActive
        ? 'bg-primary-600 text-white border-primary-600 shadow-sm'
        : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
    }`;

  const parseDateInput = (value, endOfDay = false) => {
    if (!value) return null;
    const isoString = `${value}T${endOfDay ? '23:59:59.999' : '00:00:00.000'}`;
    const parsed = new Date(isoString);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const getValidDate = (value) => {
    if (!value) return null;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const formatOptionLabel = (value) =>
    value
      .split('_')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');

  const handleClearFilters = () => {
    setAdvisorFilter('');
    setMaritalStatusFilter('');
    setHasChildrenFilter('');
    setPortalAccessFilter('');
    setCreatedDateRange({ start: '', end: '' });
    setUpdatedDateRange({ start: '', end: '' });
  };

  const getFilteredClients = () => {
    const createdStartDate = parseDateInput(createdDateRange.start);
    const createdEndDate = parseDateInput(createdDateRange.end, true);
    const updatedStartDate = parseDateInput(updatedDateRange.start);
    const updatedEndDate = parseDateInput(updatedDateRange.end, true);

    const allClients = Array.isArray(clients) ? clients : [];
    const normalizedSearchTerm = normalizeForSearch(searchTerm);

    const filtered = allClients.filter((client) => {
      const nameInfo = getClientNameInfo(client);
      const searchTargets = [
        nameInfo.firstName,
        nameInfo.middleName,
        nameInfo.lastName,
        nameInfo.displayName,
        nameInfo.legacyName,
        client?.email,
      ];

      const matchesSearch =
        normalizedSearchTerm.length === 0 ||
        searchTargets
          .map(normalizeForSearch)
          .some((value) => value.includes(normalizedSearchTerm));

      // Filter by role
      if (user?.role === 'advisor') {
        if (!matchesSearch || client.advisorId !== user.id) {
          return false;
        }
      } else if (!matchesSearch) {
        return false;
      }

      // Filter by archive status
      if (!showArchived && client.isArchived) {
        return false;
      }

      if (advisorFilter && String(client.advisorId) !== advisorFilter) {
        return false;
      }

      if (maritalStatusFilter) {
        const clientMaritalStatus = (client.maritalStatus || client.marital_status || '')
          .toString()
          .toLowerCase();
        if (clientMaritalStatus !== maritalStatusFilter) {
          return false;
        }
      }

      if (hasChildrenFilter) {
        const childrenList = Array.isArray(client.childrenInfo)
          ? client.childrenInfo
          : Array.isArray(client.children)
            ? client.children
            : Array.isArray(client.children_info)
              ? client.children_info
              : [];
        const hasChildrenValue =
          typeof client.hasChildren === 'boolean'
            ? client.hasChildren
            : typeof client.has_children === 'boolean'
              ? client.has_children
              : childrenList.length > 0;

        if (hasChildrenFilter === 'yes' && !hasChildrenValue) {
          return false;
        }

        if (hasChildrenFilter === 'no' && hasChildrenValue) {
          return false;
        }
      }

      if (portalAccessFilter) {
        const hasPortalAccess =
          typeof client.hasAccess === 'boolean'
            ? client.hasAccess
            : typeof client.has_access === 'boolean'
              ? client.has_access
              : false;

        if (portalAccessFilter === 'yes' && !hasPortalAccess) {
          return false;
        }

        if (portalAccessFilter === 'no' && hasPortalAccess) {
          return false;
        }
      }

      const createdDate = getValidDate(client.createdAt || client.created_at);
      if ((createdStartDate || createdEndDate) && !createdDate) {
        return false;
      }

      if (createdStartDate && createdDate < createdStartDate) {
        return false;
      }

      if (createdEndDate && createdDate > createdEndDate) {
        return false;
      }

      const updatedDateRaw = client.updatedAt || client.updated_at;
      const updatedDate = getValidDate(updatedDateRaw);
      const targetUpdatedDate = updatedDate || createdDate;

      if ((updatedStartDate || updatedEndDate) && !targetUpdatedDate) {
        return false;
      }

      if (updatedStartDate && targetUpdatedDate < updatedStartDate) {
        return false;
      }

      if (updatedEndDate && targetUpdatedDate > updatedEndDate) {
        return false;
      }

      return true;
    });

    return filtered;
  };

  const filteredClients = getFilteredClients();

  const sortedClients = useMemo(() => {
    const sorted = [...filteredClients];

    const getDateValue = (dateString) => {
      if (!dateString) return 0;
      const timestamp = new Date(dateString).getTime();
      return Number.isNaN(timestamp) ? 0 : timestamp;
    };

    const compareNames = (a, b, direction = 'asc') => {
      const nameA = sanitizeNamePart(formatClientName(a));
      const nameB = sanitizeNamePart(formatClientName(b));
      const comparison = nameA.localeCompare(nameB, undefined, { sensitivity: 'base' });
      return direction === 'asc' ? comparison : -comparison;
    };

    switch (sortOption) {
      case 'name-desc':
        sorted.sort((a, b) => compareNames(a, b, 'desc'));
        break;
      case 'date-created':
        sorted.sort((a, b) => getDateValue(b.createdAt) - getDateValue(a.createdAt));
        break;
      case 'date-modified':
        sorted.sort(
          (a, b) =>
            getDateValue(b.updatedAt || b.createdAt) -
            getDateValue(a.updatedAt || a.createdAt)
        );
        break;
      case 'target-revenue':
        sorted.sort((a, b) => (Number(b.targetRevenue) || 0) - (Number(a.targetRevenue) || 0));
        break;
      case 'name-asc':
      default:
        sorted.sort((a, b) => compareNames(a, b));
        break;
    }

    return sorted;
  }, [filteredClients, sortOption]);

  const handleAddClient = useCallback(async (clientData) => {
    if (!agentTrackerId) {
      const errorMessage = agentTrackerError || AGENT_PROFILE_ERROR;
      setAgentTrackerError(errorMessage);
      alert(errorMessage);
      return;
    }

    logDev('Adding client:', clientData);
    setIsSubmitting(true);

    try {
      const payload = {
        ...clientData,
        advisor_id: agentTrackerId,
        advisorId: agentTrackerId,
        created_by: user?.id,
        createdBy: user?.id,
      };
      await addClient(payload);
      setIsAddModalOpen(false);
    } catch (error) {
      console.error('Error adding client:', error);
      alert(`Failed to add client: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  }, [addClient, agentTrackerError, agentTrackerId, user?.id]);

  const handleUpdateClient = useCallback(async (clientData) => {
    logDev('Updating client:', clientData);
    setIsSubmitting(true);

    try {
      await updateClient(selectedClient.id, clientData);
      setIsEditModalOpen(false);
      setSelectedClient(null);
    } catch (error) {
      console.error('Error updating client:', error);
      alert(`Failed to update client: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  }, [updateClient, selectedClient]);

  const handleEdit = useCallback((client) => {
    const normalizedClient = normalizeRelationshipData(client);
    setSelectedClient(normalizedClient);
    setIsEditModalOpen(true);
  }, []);

  const handleDelete = useCallback(async (clientId) => {
    if (window.confirm('Are you sure you want to delete this client?')) {
      try {
        logDev('Deleting client:', clientId);
        await deleteClient(clientId);
      } catch (error) {
        console.error('Error deleting client:', error);
        alert(`Failed to delete client: ${error.message}`);
      }
    }
  }, [deleteClient]);

  const handleTogglePortalAccess = useCallback(async (clientId, currentAccess) => {
    try {
      logDev('Toggling portal access:', clientId, !currentAccess);
      await updateClient(clientId, {
        has_access: !currentAccess,
        updated_at: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error toggling portal access:', error);
      alert(`Failed to update portal access: ${error.message}`);
    }
  }, [updateClient]);

  const handleToggleArchive = useCallback(async (clientId, isArchived) => {
    try {
      logDev('Toggling archive status:', clientId, !isArchived);
      await updateClient(clientId, {
        is_archived: !isArchived,
        last_activity: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      });
    } catch (error) {
      console.error('Error toggling archive status:', error);
      alert(`Failed to update archive status: ${error.message}`);
    }
  }, [updateClient]);

  const toggleViewMode = useCallback(() => {
    setViewMode((prev) => (prev === 'grid' ? 'list' : 'grid'));
  }, []);

  const formatCurrency = (amount) => {
    if (!amount) return '';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-3xl font-heading font-bold text-gray-900">Client Management</h1>
              <p className="text-gray-600">Manage your client relationships and information</p>
            </div>
            <button
              onClick={() => setIsAddModalOpen(true)}
              className="btn-primary flex items-center space-x-2"
            >
              <SafeIcon icon={FiPlus} className="w-5 h-5" />
              <span>Add Client</span>
            </button>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search clients..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-input pl-10"
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto sm:items-end">
              <div className="w-full sm:w-56">
                <label htmlFor="client-sort" className="block text-sm font-medium text-gray-700 mb-1">
                  Sort by
                </label>
                <select
                  id="client-sort"
                  value={sortOption}
                  onChange={(e) => setSortOption(e.target.value)}
                  className="form-input"
                >
                  <option value="name-asc">Name (A → Z)</option>
                  <option value="name-desc">Name (Z → A)</option>
                  <option value="date-created">Date Created (Newest)</option>
                  <option value="date-modified">Date Modified (Newest)</option>
                  <option value="target-revenue">Target Revenue (High → Low)</option>
                </select>
              </div>
              <button
                onClick={toggleViewMode}
                className={`btn-secondary flex items-center justify-center sm:justify-start space-x-2 w-full sm:w-auto ${viewMode === 'list' ? 'bg-primary-100 text-primary-800' : ''}`}
              >
                <SafeIcon icon={viewMode === 'grid' ? FiList : FiGrid} className="w-4 h-4" />
                <span>{viewMode === 'grid' ? 'List View' : 'Grid View'}</span>
              </button>
              <button
                onClick={() => setShowFilters((prev) => !prev)}
                className={`btn-secondary flex items-center justify-center sm:justify-start space-x-2 w-full sm:w-auto ${
                  showFilters ? 'bg-primary-100 text-primary-800' : ''
                }`}
              >
                <SafeIcon icon={FiFilter} className="w-4 h-4" />
                <span>{showFilters ? 'Hide Filters' : 'Show Filters'}</span>
                {hasActiveFilters && (
                  <span className="ml-1 inline-flex h-2 w-2 rounded-full bg-primary-500" aria-hidden="true" />
                )}
              </button>
              <button
                onClick={() => setShowArchived(!showArchived)}
                className={`btn-secondary flex items-center justify-center sm:justify-start space-x-2 w-full sm:w-auto ${showArchived ? 'bg-primary-100 text-primary-800' : ''}`}
              >
                <SafeIcon icon={showArchived ? FiToggleRight : FiArchive} className="w-4 h-4" />
                <span>{showArchived ? 'Showing Archived' : 'Show Archived'}</span>
              </button>
            </div>
          </div>
          {agentTrackerError && (
            <div className="mb-4 rounded-md bg-red-50 p-4 border border-red-200 text-red-800">
              {agentTrackerError}
            </div>
          )}
          {showFilters && (
            <div className="bg-white rounded-lg shadow-soft p-4 mb-6">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Advisor</label>
                  <select
                    value={advisorFilter}
                    onChange={(e) => setAdvisorFilter(e.target.value)}
                    className="form-input"
                  >
                  <option value="">All advisors</option>
                  {advisors.map((advisor) => (
                    <option key={advisor.id} value={String(advisor.id)}>
                      {advisor.name || advisor.fullName || advisor.email || 'Advisor'}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Marital Status</label>
                  <select
                    value={maritalStatusFilter}
                    onChange={(e) => setMaritalStatusFilter(e.target.value)}
                    className="form-input"
                  >
                  <option value="">All marital statuses</option>
                  {maritalStatusOptions.map((status) => (
                    <option key={status} value={status}>
                      {formatOptionLabel(status)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Has Children</label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      setHasChildrenFilter((prev) => (prev === 'yes' ? '' : 'yes'))
                    }
                    className={toggleButtonClasses(hasChildrenFilter === 'yes')}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setHasChildrenFilter((prev) => (prev === 'no' ? '' : 'no'))
                    }
                    className={toggleButtonClasses(hasChildrenFilter === 'no')}
                  >
                    No
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Portal Access</label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      setPortalAccessFilter((prev) => (prev === 'yes' ? '' : 'yes'))
                    }
                    className={toggleButtonClasses(portalAccessFilter === 'yes')}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setPortalAccessFilter((prev) => (prev === 'no' ? '' : 'no'))
                    }
                    className={toggleButtonClasses(portalAccessFilter === 'no')}
                  >
                    No
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Created Date</label>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <input
                    type="date"
                    value={createdDateRange.start}
                    onChange={(e) =>
                      setCreatedDateRange((prev) => ({ ...prev, start: e.target.value }))
                    }
                    className="form-input"
                  />
                  <input
                    type="date"
                    value={createdDateRange.end}
                    onChange={(e) =>
                      setCreatedDateRange((prev) => ({ ...prev, end: e.target.value }))
                    }
                    className="form-input"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Updated Date</label>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <input
                    type="date"
                    value={updatedDateRange.start}
                    onChange={(e) =>
                      setUpdatedDateRange((prev) => ({ ...prev, start: e.target.value }))
                    }
                    className="form-input"
                  />
                  <input
                    type="date"
                    value={updatedDateRange.end}
                    onChange={(e) =>
                      setUpdatedDateRange((prev) => ({ ...prev, end: e.target.value }))
                    }
                    className="form-input"
                  />
                </div>
              </div>
              </div>
              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={handleClearFilters}
                  className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!hasActiveFilters}
                >
                  Clear Filters
                </button>
              </div>
            </div>
            )}
          </motion.div>

          {sortedClients.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <div className="p-8 text-center bg-white rounded-lg shadow-soft">
              <p className="text-gray-600">No clients found matching your search criteria.</p>
            </div>
          </motion.div>
        ) : viewMode === 'grid' ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {sortedClients.map((client) => {
              const nameInfo = getClientNameInfo(client);
              const clientDisplayName = formatClientName(client, nameInfo) || 'Client';

              return (
                <div
                  key={client.id}
                  className={`card hover:shadow-medium transition-shadow ${client.isArchived ? 'border-dashed border-gray-300 bg-gray-50' : ''}`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <img src={getProfileImageUrl(client)} alt={clientDisplayName} className="w-12 h-12 rounded-full object-cover" />
                      <div>
                        <div className="flex items-center">
                          <Link
                            to={`/clients/${client.id}`}
                            className="font-semibold text-gray-900 hover:text-primary-600 transition-colors"
                          >
                            {clientDisplayName}
                          </Link>
                          {client.isArchived && (
                            <span className="ml-2 px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded-full">
                              Archived
                            </span>
                          )}
                        </div>
                        <div className="flex items-center space-x-2">
                          <StatusBadge status={client.status} />
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Link to={`/clients/${client.id}`} className="p-2 text-gray-400 hover:text-primary-600 transition-colors">
                        <SafeIcon icon={FiEye} className="w-4 h-4" />
                      </Link>
                      <button
                        onClick={() => handleEdit(client)}
                        className="p-2 text-gray-400 hover:text-primary-600 transition-colors"
                      >
                        <SafeIcon icon={FiEdit} className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(client.id)}
                        className="p-2 text-gray-400 hover:text-danger-600 transition-colors"
                      >
                        <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <SafeIcon icon={FiMail} className="w-4 h-4" />
                      <span>{client.email}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <SafeIcon icon={FiPhone} className="w-4 h-4" />
                      <span>{client.phone}</span>
                    </div>
                    {client.address && (
                      <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <SafeIcon icon={FiMapPin} className="w-4 h-4" />
                        <span>{client.address}</span>
                      </div>
                    )}
                    {client.employerName && (
                      <div className="flex items-center space-x-2 text-sm text-gray-600">
                        <SafeIcon icon={FiBriefcase} className="w-4 h-4" />
                        <span>{client.employerName}</span>
                      </div>
                    )}
                    {client.targetRevenue && (
                      <div className="flex items-center space-x-2 text-sm text-success-600 font-medium">
                        <SafeIcon icon={FiDollarSign} className="w-4 h-4" />
                        <span>Target Revenue: {formatCurrency(client.targetRevenue)}</span>
                      </div>
                    )}

                    {/* Portal Access Toggle */}
                    <div className="flex items-center justify-between pt-2">
                      <span className="text-sm text-gray-600">Portal Access</span>
                      <Toggle
                        enabled={client.hasAccess || false}
                        onChange={() => handleTogglePortalAccess(client.id, client.hasAccess)}
                        size="sm"
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <div className="flex items-center space-x-2">
                      <SafeIcon icon={FiCalendar} className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-500">Added {client.createdAt}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleToggleArchive(client.id, client.isArchived)}
                        className={`p-2 rounded-lg ${client.isArchived ? 'text-primary-600 hover:bg-primary-50' : 'text-gray-400 hover:bg-gray-50'}`}
                        title={client.isArchived ? "Unarchive" : "Archive"}
                      >
                        <SafeIcon icon={FiArchive} className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="space-y-4"
          >
            <div className="bg-white rounded-lg shadow-soft overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Client
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Contact
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell"
                      >
                        Employer
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell"
                      >
                        Revenue Target
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell"
                      >
                        Added
                      </th>
                      <th scope="col" className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Portal Access
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {sortedClients.map((client) => {
                      const nameInfo = getClientNameInfo(client);
                      const clientDisplayName = formatClientName(client, nameInfo) || 'Client';

                      return (
                        <tr key={client.id} className={client.isArchived ? 'bg-gray-50' : ''}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <img
                                src={getProfileImageUrl(client)}
                                alt={clientDisplayName}
                                className="w-10 h-10 rounded-full object-cover"
                              />
                              <div className="ml-3">
                                <div className="flex items-center gap-2">
                                  <Link
                                    to={`/clients/${client.id}`}
                                    className="text-sm font-semibold text-gray-900 hover:text-primary-600 transition-colors"
                                  >
                                    {clientDisplayName}
                                  </Link>
                                  {client.isArchived && (
                                    <span className="px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded-full">Archived</span>
                                  )}
                                </div>
                                <div className="mt-1">
                                  <StatusBadge status={client.status} />
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2 text-sm text-gray-900">
                              <SafeIcon icon={FiMail} className="w-4 h-4 text-gray-400" />
                              <span>{client.email}</span>
                            </div>
                            <div className="mt-1 flex items-center gap-2 text-sm text-gray-500">
                              <SafeIcon icon={FiPhone} className="w-4 h-4 text-gray-400" />
                              <span>{client.phone}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 hidden lg:table-cell">
                            {client.employerName || '—'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-success-600 font-medium hidden md:table-cell">
                            {client.targetRevenue ? formatCurrency(client.targetRevenue) : '—'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 hidden md:table-cell">
                            <div className="flex items-center gap-2">
                              <SafeIcon icon={FiCalendar} className="w-4 h-4 text-gray-400" />
                              <span>Added {client.createdAt}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <Toggle
                              enabled={client.hasAccess || false}
                              onChange={() => handleTogglePortalAccess(client.id, client.hasAccess)}
                              size="sm"
                            />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center justify-end gap-2">
                              <Link
                                to={`/clients/${client.id}`}
                                className="p-2 text-gray-400 hover:text-primary-600 transition-colors"
                              >
                                <SafeIcon icon={FiEye} className="w-4 h-4" />
                              </Link>
                              <button
                                onClick={() => handleEdit(client)}
                                className="p-2 text-gray-400 hover:text-primary-600 transition-colors"
                              >
                                <SafeIcon icon={FiEdit} className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(client.id)}
                                className="p-2 text-gray-400 hover:text-danger-600 transition-colors"
                              >
                                <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleToggleArchive(client.id, client.isArchived)}
                                className={`p-2 rounded-lg ${client.isArchived ? 'text-primary-600 hover:bg-primary-50' : 'text-gray-400 hover:bg-gray-50'}`}
                                title={client.isArchived ? "Unarchive" : "Archive"}
                              >
                                <SafeIcon icon={FiArchive} className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}

        {/* Add Client Modal */}
        <Modal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          title="Add New Client"
          size="xl"
        >
          <ClientForm
            onSubmit={handleAddClient}
            onCancel={() => setIsAddModalOpen(false)}
            isEditing={false}
          />
        </Modal>

        {/* Edit Client Modal */}
        <Modal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedClient(null);
          }}
          title="Edit Client"
          size="xl"
        >
          {selectedClient && (
            <ClientForm
              initialData={selectedClient}
              onSubmit={handleUpdateClient}
              onCancel={() => {
                setIsEditModalOpen(false);
                setSelectedClient(null);
              }}
              isEditing={true}
            />
          )}
        </Modal>
      </div>
    </div>
  );
};

export default ClientManagement;
